import { useCallback } from 'react';
import { useHistory } from 'react-router-dom';
import { reportError } from '../../lib/reportError';
import { PaymentSource, CVV_ERROR } from '../../constants';
import { hrefFor } from '../../routers';
import { useCart } from '../useCart';
import { useErrorModal } from '../useErrorModal';
import { usePlaceOrderMutation } from './usePlaceOrderMutation';
import { useAffirmCheckout } from './useAffirmCheckout';
import { useCheckoutError } from './useCheckoutError';

export const useCheckout = () => {
  const history = useHistory();
  const { showErrorModal } = useErrorModal();
  const { cart } = useCart();
  const [placeOrder] = usePlaceOrderMutation();
  const { placeAffirmOrder } = useAffirmCheckout();
  const { setCheckoutError } = useCheckoutError();

  const isCartValid =
    Boolean(cart.id) && cart.attributes.cartItems.every(item => !item.data.attributes.error);
  const isShippingAddressSelected = Boolean(cart.attributes.addressId);
  const isShippingMethodSelected = Boolean(cart.attributes.selectedShippingMethodId);
  const isPaymentMethodSelected =
    Boolean(cart.attributes.paymentMethodId) ||
    cart.attributes.paymentSource === PaymentSource.Affirm;

  const canPlaceOrder =
    isCartValid && isShippingAddressSelected && isShippingMethodSelected && isPaymentMethodSelected;

  const handlePlaceOrder = useCallback(async () => {
    if (!canPlaceOrder) return;

    try {
      const { data } =
        cart.attributes.paymentSource === PaymentSource.Affirm
          ? await placeAffirmOrder()
          : await placeOrder();
      data &&
        history.push(
          hrefFor.OrderConfirmationPage({
            orderNumber: data.checkout.data[0].attributes.orderNumber
          })
        );
    } catch (error) {
      if (error.reason !== 'canceled') {
        const errorMessage = error.networkError?.result?.errors?.[0]?.detail;
        setCheckoutError(errorMessage);

        if (errorMessage !== CVV_ERROR) {
          showErrorModal();
        }

        reportError(error);
      }
    }
  }, [
    history,
    canPlaceOrder,
    placeOrder,
    showErrorModal,
    cart,
    placeAffirmOrder,
    setCheckoutError
  ]);

  return {
    isCartValid,
    isShippingAddressSelected,
    isShippingMethodSelected,
    isPaymentMethodSelected,
    canPlaceOrder,
    handlePlaceOrder
  };
};
